import { faChevronCircleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { useCheckoutScreenContext } from '../../hooks/useCheckoutScreenContext';
import GooglePayButtonContainer from '../../components/paymentTypes/mastercardDirectPayment/googlePayButtonContainer';
import { findUnschedulableItems } from '../../helpers/categorySchedule';
import { errorOperations } from '../../state/features/error';
import { loaderOperations } from '../../state/features/loader';
import { promotionsOperations } from '../../state/features/promotions';
import { usePromotionsContext } from '../../hooks/usePromotionsContext';
import { getPromoCodeVerificationPayload } from '../../features/promotions/helpers/utils';

interface ICheckoutCtaButtonProps {
  placeOrderEnabled: boolean;
  outOfStockItems: any[];
  recentOrder: any;
  orderPayload: any;
  storeOpen: boolean;
  activeStep: any;
  deliveryTime: Date;
  clickAction: () => void;
  backAction: () => void;
  showLoader: () => void;
  hideLoader: () => void;
  updateError: (error: any) => void;
  verifyPromoCode: (payload: any) => void;
}

const CheckoutCtaButton = ({
  placeOrderEnabled,
  outOfStockItems,
  orderPayload,
  recentOrder,
  storeOpen,
  activeStep,
  deliveryTime,
  clickAction,
  backAction,
  showLoader,
  hideLoader,
  updateError,
  verifyPromoCode,
}: ICheckoutCtaButtonProps) => {
  const { paymentTypes, menu } = useSelector((state: RootState) => state);
  const { allow_order_when_closed: allowOrderWhenClosed = false } = useSelector(
    (state: RootState) => state.company?.configuration || {}
  );
  const { promotionAppliedCart, availablePromotions } = useSelector(
    (state: RootState) => state.promotions
  );
  const { sapaad_company_id: sapaadCompanyId } = useSelector(
    (state: RootState) => state.company
  );
  const { sapaad_customer_id: sapaadCustomerId } = useSelector(
    (state: RootState) => state.user
  );
  const { appliedPromotionsData, appliedPromoCode, resetPromocode } =
    usePromotionsContext();

  const intl = useIntl();
  const { mpgsAddonPaymentType, checkoutPageConfigs } =
    useCheckoutScreenContext();

  const currentCartItems = useMemo(() => {
    return promotionAppliedCart.length > 0
      ? promotionAppliedCart
      : JSON.parse(localStorage.getItem('cartItems') || '[]');
  }, [promotionAppliedCart]);

  const unschedulableItems = useMemo(() => {
    if (allowOrderWhenClosed) {
      return findUnschedulableItems(
        deliveryTime,
        currentCartItems,
        menu.categories
      );
    } else {
      return [];
    }
  }, [
    deliveryTime,
    currentCartItems,
    menu.categories,
    allowOrderWhenClosed,
    storeOpen,
  ]);

  const disableCtaButton: boolean = useMemo(() => {
    return (
      (!storeOpen && !allowOrderWhenClosed) ||
      !placeOrderEnabled ||
      !currentCartItems?.length ||
      (outOfStockItems.length && activeStep === 2) ||
      (recentOrder && activeStep === 2) ||
      !checkoutPageConfigs.showPaymentMethods ||
      unschedulableItems.length
    );
  }, [
    storeOpen,
    allowOrderWhenClosed,
    placeOrderEnabled,
    currentCartItems,
    outOfStockItems,
    recentOrder,
    checkoutPageConfigs.showPaymentMethods,
    unschedulableItems,
  ]);

  const checkPromoCodeValidity = async () => {
    if (appliedPromoCode === '') {
      return true;
    }
    showLoader();
    const promoVerificationPayload = getPromoCodeVerificationPayload(
      appliedPromoCode,
      appliedPromotionsData.availablePromoCodes,
      sapaadCompanyId,
      sapaadCustomerId
    );

    let isPromoCodeValid = false;
    try {
      const result = (await verifyPromoCode(promoVerificationPayload)) as any;
      isPromoCodeValid = result.payload?.data?.is_valid;
    } catch (error) {
      console.error('An unexpected error occurred:', error);
      isPromoCodeValid = false;
      resetPromocode();
    } finally {
      hideLoader();
    }
    return isPromoCodeValid;
  };

  const ctaButtonClickHandler = async () => {
    if (availablePromotions.length === 0) {
      // No promotions available, proceed with order placement
      clickAction();
      return;
    }
    const isPromoCodeValid = await checkPromoCodeValidity();
    if (!isPromoCodeValid) {
      updateError({
        title: intl.formatMessage({
          id: 'error.coupons_invalid',
          defaultMessage: 'Please enter a valid promo code',
        }),
        message: intl.formatMessage({
          id: 'error.coupons_invalid_message',
          defaultMessage:
            'This promo has reached its maximum usage. Please try another code.',
        }),
      });

      return;
    }
    clickAction();
  };

  const isMastercardDirectPayment = useMemo(() => {
    const masterCardPaymentType = paymentTypes.find(
      (p: any) => p.payment_type === 'mastercard'
    );
    if (orderPayload?.payment?.payment_type === 'mastercard') {
      return masterCardPaymentType?.direct_payment_enabled && true;
    }
    return false;
  }, [orderPayload]);

  if (
    activeStep === 2 &&
    isMastercardDirectPayment &&
    !disableCtaButton &&
    mpgsAddonPaymentType !== 'Card'
  ) {
    if (mpgsAddonPaymentType === 'Google Pay') {
      return <GooglePayButtonContainer orderPayload={orderPayload} />;
    } else if (mpgsAddonPaymentType === 'Apple Pay') {
      return (
        <button
          className='apple-pay-button apple-pay-button-black w-100'
          style={{ height: '45px' }}
          onClick={ctaButtonClickHandler}
        ></button>
      );
    } else {
      return null;
    }
  } else {
    return (
      <React.Fragment>
        <button
          className='button btn btn-outline-primary mx-1'
          onClick={backAction}
        >
          <FormattedMessage id='global.back' defaultMessage='Back' />
        </button>
        <button
          className='button btn btn-primary mx-1'
          onClick={ctaButtonClickHandler}
          disabled={disableCtaButton}
        >
          {activeStep === 2 ? (
            <FormattedMessage
              id='checkout.place_order'
              defaultMessage='Place order'
            />
          ) : (
            <FormattedMessage id='global.next' defaultMessage='Next' />
          )}
          <span className='ml-3'>
            <FontAwesomeIcon icon={faChevronCircleRight} />
          </span>
        </button>
      </React.Fragment>
    );
  }
};

const mapDispatchToProps = {
  verifyPromoCode: promotionsOperations.reValidatePromoCode,
  showLoader: loaderOperations.showLoader,
  hideLoader: loaderOperations.hideLoader,
  updateError: errorOperations.updateError,
};

export default connect(null, mapDispatchToProps)(CheckoutCtaButton);
